import { z } from 'zod';

const PageTitle = z.string().min(1).max(200);
const PageIcon = z.string().max(40);

// ── Full projection (single page) ──────────────────────────────────────────

export const PageSchema = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid(),
  projectId: z.string().uuid().nullable(),
  parentPageId: z.string().uuid().nullable(),
  title: z.string(),
  /** TipTap JSON. */
  body: z.unknown(),
  /** Plaintext mirror — useful for previews / search snippets. */
  bodyText: z.string(),
  icon: z.string().nullable(),
  coverImageId: z.string().uuid().nullable(),
  position: z.number().int().nonnegative(),
  createdBy: z.string().uuid(),
  updatedBy: z.string().uuid(),
  /** Display names for provenance — resolved by the detail endpoint; null when
   *  the author/editor is no longer resolvable. */
  createdByName: z.string().nullable(),
  updatedByName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PageProjection = z.infer<typeof PageSchema>;

// ── Tree node (list-by-scope) ──────────────────────────────────────────────
// Smaller payload than the full page — no body, just metadata for the
// sidebar tree.  Body is loaded on click via `GET /api/pages/:id`.

export const PageTreeNodeSchema = z.object({
  id: z.string().uuid(),
  parentPageId: z.string().uuid().nullable(),
  title: z.string(),
  icon: z.string().nullable(),
  position: z.number().int().nonnegative(),
  hasChildren: z.boolean(),
  updatedAt: z.string(),
});
export type PageTreeNodeProjection = z.infer<typeof PageTreeNodeSchema>;

export const PageTreeSchema = z.array(PageTreeNodeSchema);

// ── Create / update ────────────────────────────────────────────────────────

export const CreatePageSchema = z
  .object({
    /** Required when creating a project-scoped page; omit for team-scoped. */
    projectId: z.string().uuid().nullable().optional(),
    parentPageId: z.string().uuid().nullable().optional(),
    title: PageTitle.optional(),
    icon: PageIcon.nullable().optional(),
  })
  .strict();
export type CreatePageInput = z.infer<typeof CreatePageSchema>;

export const UpdatePageSchema = z
  .object({
    title: PageTitle.optional(),
    /** TipTap JSON.  Send the whole document — partial-merge is server-naive. */
    body: z.unknown().optional(),
    /** Plaintext mirror — frontend computes from TipTap and ships alongside `body`. */
    bodyText: z.string().max(200_000).optional(),
    icon: PageIcon.nullable().optional(),
    coverImageId: z.string().uuid().nullable().optional(),
    parentPageId: z.string().uuid().nullable().optional(),
    position: z.number().int().nonnegative().optional(),
  })
  .strict();
export type UpdatePageInput = z.infer<typeof UpdatePageSchema>;
